import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ReportTabsData } from '@/features/reports/types/reports';
import { Empty } from '../ReportDetailPagePrimitives';

const PIE_COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#6366F1'];

function StatCard({ title, value }: { title: string; value: string | number }) {
  return (
    <div className="rounded-2xl border border-[var(--dash-line)] bg-white p-5 text-left shadow-sm transition hover:shadow-md">
      <h3 className="m-0 mb-3 text-sm font-semibold text-[var(--dash-ink)]">{title}</h3>
      <span className="text-xl font-black tracking-tight text-[var(--dash-ink)]">{value}</span>
    </div>
  );
}

export function AnalyticsTab({ data }: { data?: ReportTabsData }) {
  const analytics = data?.campaign?.analytics;
  const chartDataRaw = data?.chartData || [];
  const ageDistribution = data?.ageDistribution || [];

  const chartData = chartDataRaw.map((item) => ({
    name: item.day,
    plays: item.successDuration || 0,
    views: item.totalViews || 0,
  }));
  const hasChartData = chartData.length > 0;

  const hasAgeData = ageDistribution.some((item) => (item.count || 0) > 0);
  const pieData = ageDistribution.map((item, index) => ({
    name: item.range,
    value: hasAgeData ? item.count || 0 : 1,
    actualCount: item.count || 0,
    color: PIE_COLORS[index % PIE_COLORS.length],
  }));

  const stats = [
    { title: 'Total Plays', value: analytics?.playTimingsResult?.totalDuration ?? '—' },
    { title: 'Total Views', value: analytics?.adPerformance?.summary?.totalViews ?? '—' },
    {
      title: 'Avg Uptime',
      value: analytics?.playTimingsResult?.successRate != null ? `${analytics.playTimingsResult.successRate}%` : '—',
    },
    {
      title: 'Dwell Time',
      value: analytics?.dwellTime != null ? analytics.dwellTime.toFixed(2) : '—',
    },
    { title: 'Engagement Score', value: analytics?.engagementScore ?? '—' },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {stats.map((s) => (
          <StatCard key={s.title} title={s.title} value={s.value} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="rounded-2xl border border-[var(--dash-line)] bg-white p-6 shadow-sm">
          <div className="mb-5 text-left">
            <h3 className="m-0 text-sm font-bold text-[var(--dash-ink)]">Plays & Views Over Time</h3>
            <p className="mt-0.5 mb-0 text-xs text-[var(--dash-muted)]">Campaign performance timeline</p>
          </div>
          <div className="h-[280px] w-full">
            {hasChartData ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 11 }} dy={8} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 11 }} width={40} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #E2E8F0',
                      borderRadius: 12,
                      fontSize: 12,
                      boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                    }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={30}
                    iconType="circle"
                    formatter={(value) => (
                      <span className="text-[11px] font-bold tracking-wider text-[var(--dash-muted)] uppercase">
                        {value}
                      </span>
                    )}
                  />
                  <Line
                    name="Plays"
                    type="monotone"
                    dataKey="plays"
                    stroke="#3B82F6"
                    strokeWidth={3}
                    dot={{ r: 3.5, fill: '#3B82F6', strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                  />
                  <Line
                    name="Views"
                    type="monotone"
                    dataKey="views"
                    stroke="#A855F7"
                    strokeWidth={3}
                    dot={{ r: 3.5, fill: '#A855F7', strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Empty text="No analytics data available yet." />
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-[var(--dash-line)] bg-white p-6 shadow-sm">
          <div className="mb-5 text-left">
            <h3 className="m-0 text-sm font-bold text-[var(--dash-ink)]">Audience Demographics</h3>
            <p className="mt-0.5 mb-0 text-xs text-[var(--dash-muted)]">Age distribution of viewers</p>
          </div>
          <div className="h-[280px] w-full">
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={0}
                    outerRadius={95}
                    paddingAngle={hasAgeData ? 4 : 0}
                    dataKey="value"
                    label={({ name }) => `Age: ${name}`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #E2E8F0',
                      borderRadius: 12,
                      fontSize: 12,
                    }}
                    formatter={(_value, _name, props) => [props.payload.actualCount, 'Count']}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Empty text="No age distribution data available yet." />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
